import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Modal,
  Form,
  Row,
  Col,
  Alert,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from '../../shared_components/Message';
import Loader from '../../shared_components/Loader';

import {
  createJitaiDecisionRule,
  updateJitaiDecisionRule,
  getJitaiVariableList,
  getJitaiMessageList,
} from '../actions/jitaiActions';
import {
  JITAI_DECISION_RULE_CREATE_RESET,
  JITAI_DECISION_RULE_UPDATE_RESET,
  JITAI_VARIABLE_LIST_RESET,
  JITAI_MESSAGE_LIST_RESET,
} from '../constants/jitaiConstants';

const UpsertJitaiDecisionRule = ({
  studyId,
  jitaiDecisionRuleList,
  updatedJitaiDecisionRule,
}) => {
  const [show, setShow] = useState(false);
  const [code, setCode] = useState('');
  const [dependentVariables, setDependentVariables] = useState([]);
  const [definition, setDefinition] = useState('');
  const [type, setType] = useState('personalizedJitaiMessage');
  const [jitaiMessages, setJitaiMessages] = useState([]);

  const dispatch = useDispatch();

  const jitaiDecisionRuleCreate = useSelector(
    (state) => state.jitaiDecisionRuleCreate
  );
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
  } = jitaiDecisionRuleCreate;

  const jitaiDecisionRuleUpdate = useSelector(
    (state) => state.jitaiDecisionRuleUpdate
  );
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = jitaiDecisionRuleUpdate;

  const jitaiVariableList = useSelector((state) => state.jitaiVariableList);
  const {
    jitaiVariables,
    loading: loadingVariables,
    error: errorVariables,
    success: successVariables,
  } = jitaiVariableList;

  const jitaiMessageList = useSelector((state) => state.jitaiMessageList);
  const {
    jitaiMessages: availableJitaiMessages,
    loading: loadingMessages,
    error: errorMessages,
    success: successMessages,
  } = jitaiMessageList;

  const isEditMode = !!updatedJitaiDecisionRule;

  useEffect(() => {
    // Initialize state when modal opens in edit mode
    if (show && isEditMode && updatedJitaiDecisionRule) {
      setCode(updatedJitaiDecisionRule.code);
      setDependentVariables(
        updatedJitaiDecisionRule.dependentVariables
          ? updatedJitaiDecisionRule.dependentVariables.map(
              (variable) => variable._id
            )
          : []
      );
      setDefinition(updatedJitaiDecisionRule.definition);
      setType(updatedJitaiDecisionRule.type || 'personalizedJitaiMessage');
      setJitaiMessages(
        updatedJitaiDecisionRule.jitai_messages
          ? updatedJitaiDecisionRule.jitai_messages.map(
              (message) => message._id
            )
          : []
      );
    }
  }, [show, isEditMode, updatedJitaiDecisionRule]);

  useEffect(() => {
    if (successCreate || successUpdate) {
      setShow(false);
      setCode('');
      setDependentVariables([]);
      setDefinition('');
      setType('personalizedJitaiMessage');
      setJitaiMessages([]);
      dispatch({ type: JITAI_DECISION_RULE_CREATE_RESET });
      dispatch({ type: JITAI_DECISION_RULE_UPDATE_RESET });
    }
  }, [successCreate, successUpdate, dispatch]);

  useEffect(() => {
    if (show) {
      if (!loadingVariables && !errorVariables && !successVariables) {
        dispatch(getJitaiVariableList(studyId));
      }
      if (type === 'personalizedJitaiMessage' && !loadingMessages && !errorMessages && !successMessages) {
        dispatch(getJitaiMessageList(studyId));
      }
    }
  }, [
    show,
    type,
    loadingVariables,
    errorVariables,
    successVariables,
    loadingMessages,
    errorMessages,
    successMessages,
    dispatch,
    studyId,
  ]);

  // Clear jitai_messages when type changes away from personalizedJitaiMessage
  useEffect(() => {
    if (type !== 'personalizedJitaiMessage') {
      setJitaiMessages([]);
    }
  }, [type]);

  const handleClose = () => {
    setShow(false);
    setCode('');
    setDependentVariables([]);
    setDefinition('');
    setType('personalizedJitaiMessage');
    setJitaiMessages([]);
  };

  const handleShow = () => {
    dispatch({ type: JITAI_VARIABLE_LIST_RESET });
    dispatch({ type: JITAI_MESSAGE_LIST_RESET });
    
    // Initialize state from updatedJitaiDecisionRule if in edit mode
    if (isEditMode && updatedJitaiDecisionRule) {
      setCode(updatedJitaiDecisionRule.code);
      setDependentVariables(
        updatedJitaiDecisionRule.dependentVariables
          ? updatedJitaiDecisionRule.dependentVariables.map(
              (variable) => variable._id
            )
          : []
      );
      setDefinition(updatedJitaiDecisionRule.definition);
      setType(updatedJitaiDecisionRule.type || 'personalizedJitaiMessage');
      setJitaiMessages(
        updatedJitaiDecisionRule.jitai_messages
          ? updatedJitaiDecisionRule.jitai_messages.map(
              (message) => message._id
            )
          : []
      );
    } else {
      // Reset to defaults for create mode
      setCode('');
      setDependentVariables([]);
      setDefinition('');
      setType('personalizedJitaiMessage');
      setJitaiMessages([]);
    }
    
    setShow(true);
  };

  const handleVariableChange = (e) => {
    const value = e.target.value;
    const selectedVariables = [...dependentVariables];

    if (e.target.checked) {
      selectedVariables.push(value);
    } else {
      const index = selectedVariables.indexOf(value);
      if (index > -1) {
        selectedVariables.splice(index, 1);
      }
    }

    setDependentVariables(selectedVariables);
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    const selectedMessages = [...jitaiMessages];

    if (e.target.checked) {
      selectedMessages.push(value);
    } else {
      const index = selectedMessages.indexOf(value);
      if (index > -1) {
        selectedMessages.splice(index, 1);
      }
    }

    setJitaiMessages(selectedMessages);
  };

  const submitHandler = (e) => {
    e.preventDefault();
    
    // Validate jitai_messages for personalizedJitaiMessage type
    if (type === 'personalizedJitaiMessage') {
      if (!jitaiMessages || jitaiMessages.length === 0) {
        alert('Please select at least one JITAI message for personalized JITAI message decision rules.');
        return;
      }
    }
    
    const submitData = {
      code,
      dependentVariables,
      definition,
      type,
    };
    
    // Only include jitai_messages if type is personalizedJitaiMessage
    if (type === 'personalizedJitaiMessage') {
      submitData.jitai_messages = jitaiMessages;
    }

    if (isEditMode) {
      dispatch(
        updateJitaiDecisionRule(studyId, updatedJitaiDecisionRule._id, submitData)
      );
    } else {
      dispatch(
        createJitaiDecisionRule(studyId, {
          ...submitData,
          implemented: false, // Always set to false for new rules
        })
      );
    }
  };

  return (
    <>
      <Button variant='primary' className='btn-sm' onClick={handleShow}>
        {isEditMode ? 'Edit' : 'Add'}
      </Button>

      <Modal show={show} onHide={handleClose} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? 'Edit' : 'Add'} Jitai Decision Rule
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingCreate || loadingUpdate ? (
            <Loader />
          ) : (
            <Form onSubmit={submitHandler}>
              <Row>
                <Col md={12}>
                  <Form.Group controlId='code'>
                    <Form.Label>Code</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter code'
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className='mt-3'>
                <Col md={12}>
                  <Form.Group controlId='type'>
                    <Form.Label>Type</Form.Label>
                    <Form.Control
                      as='select'
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      required
                    >
                      <option value='personalizedJitaiMessage'>Personalized JITAI Message</option>
                      <option value='presentJitaiMessage'>Present JITAI Message</option>
                      <option value='groupAssignment'>Group Assignment</option>
                      <option value='optionalTxPackageAssignment'>Optional Treatment Chapter Assignment</option>
                    </Form.Control>
                    <Form.Text className='text-muted'>
                      Select the type of decision rule. Personalized JITAI Message rules determine which 
                      message content to show at scheduled prompts. Present JITAI Message rules determine 
                      whether a JITAI message should be presented to the user at all (before randomization). 
                      Group Assignment rules determine treatment group assignments. Optional Treatment Chapter 
                      Assignment rules determine which optional treatment chapters to assign.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className='mt-3'>
                <Col md={12}>
                  <Form.Group controlId='dependentVariables'>
                    <Form.Label>Dependent Variables</Form.Label>
                    {loadingVariables ? (
                      <div>Loading variables...</div>
                    ) : errorVariables ? (
                      <Message variant='danger'>{errorVariables}</Message>
                    ) : !jitaiVariables || jitaiVariables.length === 0 ? (
                      <Alert variant='info'>
                        <p className='mb-2'>
                          No JITAI variables found for this study.
                        </p>
                        <p className='mb-0'>
                          Please{' '}
                          <Link
                            to={`/jitaiVariables/${studyId}`}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            create JITAI variables
                          </Link>{' '}
                          first before creating decision rules.
                        </p>
                      </Alert>
                    ) : (
                      <div
                        style={{
                          border: '1px solid #ced4da',
                          borderRadius: '0.25rem',
                          padding: '0.375rem 0.75rem',
                          maxHeight: '200px',
                          overflowY: 'auto',
                        }}
                      >
                        <Row>
                          {jitaiVariables.map((variable, index) => (
                            <Col md={4} key={variable._id} className='mb-2'>
                              <div
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.25rem',
                                  padding: '0.5rem',
                                  backgroundColor: dependentVariables.includes(
                                    variable._id
                                  )
                                    ? '#e7f3ff'
                                    : '#f8f9fa',
                                  transition: 'all 0.2s ease',
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#007bff';
                                  e.currentTarget.style.boxShadow =
                                    '0 0 0 0.2rem rgba(0, 123, 255, 0.25)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#dee2e6';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                                onClick={() => {
                                  const event = {
                                    target: {
                                      value: variable._id,
                                      checked: !dependentVariables.includes(
                                        variable._id
                                      ),
                                    },
                                  };
                                  handleVariableChange(event);
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    pointerEvents: 'none',
                                  }}
                                >
                                  <input
                                    type='checkbox'
                                    id={`variable-${variable._id}`}
                                    checked={dependentVariables.includes(
                                      variable._id
                                    )}
                                    readOnly
                                    style={{
                                      marginRight: '0.5rem',
                                      pointerEvents: 'none',
                                    }}
                                  />
                                  <OverlayTrigger
                                    placement='top'
                                    overlay={
                                      <Tooltip id={`tooltip-${variable._id}`}>
                                        <div
                                          style={{
                                            textAlign: 'left',
                                            maxWidth: '300px',
                                          }}
                                        >
                                          <strong>{variable.code}</strong>
                                          <br />
                                          {variable.definition}
                                        </div>
                                      </Tooltip>
                                    }
                                  >
                                    <span
                                      style={{
                                        cursor: 'help',
                                        pointerEvents: 'auto',
                                      }}
                                    >
                                      {variable.code}
                                    </span>
                                  </OverlayTrigger>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {type === 'personalizedJitaiMessage' && (
                <Row className='mt-3'>
                  <Col md={12}>
                    <Form.Group controlId='jitaiMessages'>
                      <Form.Label>JITAI Messages</Form.Label>
                      {loadingMessages ? (
                        <div>Loading messages...</div>
                      ) : errorMessages ? (
                        <Message variant='danger'>{errorMessages}</Message>
                      ) : !availableJitaiMessages || availableJitaiMessages.length === 0 ? (
                        <Alert variant='info'>
                          <p className='mb-2'>
                            No JITAI messages found for this study.
                          </p>
                          <p className='mb-0'>
                            Please{' '}
                            <Link
                              to={`/jitaiMessages/${studyId}`}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              create JITAI messages
                            </Link>{' '}
                            first before creating decision rules that return messages.
                          </p>
                        </Alert>
                      ) : (
                        <div
                          style={{
                            border: '1px solid #ced4da',
                            borderRadius: '0.25rem',
                            padding: '0.375rem 0.75rem',
                            maxHeight: '200px',
                            overflowY: 'auto',
                          }}
                        >
                          <Row>
                            {availableJitaiMessages.map((message, index) => (
                              <Col md={4} key={message._id} className='mb-2'>
                                <div
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.25rem',
                                    padding: '0.5rem',
                                    backgroundColor: jitaiMessages.includes(
                                      message._id
                                    )
                                      ? '#e7f3ff'
                                      : '#f8f9fa',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#007bff';
                                    e.currentTarget.style.boxShadow =
                                      '0 0 0 0.2rem rgba(0, 123, 255, 0.25)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#dee2e6';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                  onClick={() => {
                                    const event = {
                                      target: {
                                        value: message._id,
                                        checked: !jitaiMessages.includes(
                                          message._id
                                        ),
                                      },
                                    };
                                    handleMessageChange(event);
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      pointerEvents: 'none',
                                    }}
                                  >
                                    <input
                                      type='checkbox'
                                      id={`message-${message._id}`}
                                      checked={jitaiMessages.includes(
                                        message._id
                                      )}
                                      readOnly
                                      style={{
                                        marginRight: '0.5rem',
                                        pointerEvents: 'none',
                                      }}
                                    />
                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip id={`tooltip-${message._id}`}>
                                          <div
                                            style={{
                                              textAlign: 'left',
                                              maxWidth: '300px',
                                            }}
                                          >
                                            <strong>{message.code}</strong>
                                            <br />
                                            {message.message}
                                          </div>
                                        </Tooltip>
                                      }
                                    >
                                      <span
                                        style={{
                                          cursor: 'help',
                                          pointerEvents: 'auto',
                                        }}
                                      >
                                        {message.code}
                                      </span>
                                    </OverlayTrigger>
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      )}
                      <Form.Text className='text-muted'>
                        Select the messages that could be returned by this decision rule. 
                        The rule should return the ID of one of the selected messages instead of true/false.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Row className='mt-3'>
                <Col md={12}>
                  <Form.Group controlId='definition'>
                    <Form.Label>Definition</Form.Label>
                    <Form.Control
                      as='textarea'
                      rows={8}
                      placeholder={
                        type === 'personalizedJitaiMessage'
                          ? 'Enter how this decision rule should be implemented based on the dependent variables. Provide clear instructions for developers to implement the logic that determines which message ID (from the selected messages above) should be returned by this rule.'
                          : 'Enter how this decision rule should be implemented based on the dependent variables. Provide clear instructions for developers to implement the logic that determines when this rule should trigger true/false.'
                      }
                      value={definition}
                      onChange={(e) => setDefinition(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              {errorCreate && <Message variant='danger'>{errorCreate}</Message>}
              {errorUpdate && <Message variant='danger'>{errorUpdate}</Message>}

              <div className='mt-3'>
                <Button
                  type='submit'
                  variant='primary'
                  style={{ marginRight: '12px' }}
                  disabled={
                    !jitaiVariables || 
                    jitaiVariables.length === 0 ||
                    (type === 'personalizedJitaiMessage' && (!jitaiMessages || jitaiMessages.length === 0))
                  }
                >
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
                <Button variant='secondary' onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UpsertJitaiDecisionRule;
